import { ObjectiveFilter, ObjectiveFilterOperator } from 'app/objectives/types'

export type FilterDraftType =
  | 'event'
  | 'eventCount'
  | 'fieldExist'
  | 'sequence'
  | 'timeBased'
  | 'user'
  | 'userHistory'

interface BaseFilterDraft {
  id: number
  type: FilterDraftType
}

export interface PropertyFilterDraft extends BaseFilterDraft {
  operator: ObjectiveFilterOperator
  property: string
  type: 'event' | 'user'
  value: string
}

export interface FieldExistFilterDraft extends BaseFilterDraft {
  property: string
  type: 'fieldExist'
}

export interface TimeBasedFilterDraft extends BaseFilterDraft {
  property: string
  type: 'timeBased'
  value: string
}

export interface EventCountFilterDraft extends BaseFilterDraft {
  event: string
  operator: ObjectiveFilterOperator
  type: 'eventCount'
  value: string
}

export interface HistoryConditionDraft {
  operator: ObjectiveFilterOperator
  property: string
  value: string
}

export interface UserHistoryFilterDraft extends BaseFilterDraft {
  conditions: HistoryConditionDraft[]
  event: string
  operator: ObjectiveFilterOperator
  type: 'userHistory'
  value: string
}

export interface SequenceFilterDraft extends BaseFilterDraft {
  steps: SequenceStepDraft[]
  type: 'sequence'
}

export interface SequenceStepDraft {
  event: string
  negate: boolean
}

export type FilterDraft =
  | FieldExistFilterDraft
  | EventCountFilterDraft
  | PropertyFilterDraft
  | SequenceFilterDraft
  | TimeBasedFilterDraft
  | UserHistoryFilterDraft

export const numericOperators: ObjectiveFilterOperator[] = [
  'greaterThan',
  'greaterThanOrEqual',
  'lessThan',
  'lessThanOrEqual',
]

export const isNumericFilterOperator = (operator: ObjectiveFilterOperator) =>
  [...numericOperators, 'withinLastDays'].includes(operator)

export const createFilterDraft = (id: number, type: FilterDraftType = 'event'): FilterDraft => {
  switch (type) {
    case 'eventCount':
      return { event: '', id, operator: 'greaterThanOrEqual', type, value: '1' }
    case 'userHistory':
      return { conditions: [], event: '', id, operator: 'greaterThanOrEqual', type, value: '1' }
    case 'fieldExist':
      return { id, property: '', type }
    case 'timeBased':
      return { id, property: '', type, value: '30' }
    case 'sequence':
      return {
        id,
        steps: [
          { event: '', negate: false },
          { event: '', negate: false },
        ],
        type,
      }
    default:
      return { id, operator: 'equals', property: '', type, value: '' }
  }
}

const isValidNumber = (value: string) => value.trim().length > 0 && Number.isFinite(Number(value))

export const isFilterDraftValid = (filter: FilterDraft) => {
  switch (filter.type) {
    case 'event':
    case 'user':
      return (
        filter.property.length > 0 &&
        filter.value.trim().length > 0 &&
        (!isNumericFilterOperator(filter.operator) || isValidNumber(filter.value))
      )
    case 'fieldExist':
      return filter.property.length > 0
    case 'timeBased':
      return filter.property.length > 0 && isValidNumber(filter.value)
    case 'eventCount':
      return filter.event.length > 0 && isValidNumber(filter.value)
    case 'userHistory':
      return (
        filter.event.length > 0 &&
        isValidNumber(filter.value) &&
        filter.conditions.every(
          (condition) =>
            condition.property.length > 0 &&
            condition.value.trim().length > 0 &&
            (!isNumericFilterOperator(condition.operator) || isValidNumber(condition.value)),
        )
      )
    case 'sequence':
      return filter.steps.length >= 2 && filter.steps.every((step) => step.event.length > 0)
  }
}

export const toObjectiveFilter = (draft: FilterDraft): ObjectiveFilter => {
  switch (draft.type) {
    case 'event':
    case 'user':
      return {
        operator: draft.operator,
        property: draft.property,
        type: draft.type,
        value: isNumericFilterOperator(draft.operator) ? Number(draft.value) : draft.value.trim(),
      }
    case 'fieldExist':
      return { operator: 'exists', property: draft.property, type: 'event' }
    case 'timeBased':
      return {
        operator: 'withinLastDays',
        property: draft.property,
        type: 'event',
        value: Number(draft.value),
      }
    case 'eventCount':
      return {
        event: draft.event,
        occurrences: { operator: draft.operator, value: Number(draft.value) },
        type: 'eventCount',
      }
    case 'userHistory':
      return {
        conditions: draft.conditions.map((condition) => ({
          ...condition,
          value: isNumericFilterOperator(condition.operator)
            ? Number(condition.value)
            : condition.value.trim(),
        })),
        event: draft.event,
        occurrences: { operator: draft.operator, value: Number(draft.value) },
        type: 'userHistory',
      }
    case 'sequence':
      return {
        steps: draft.steps.map(({ event, negate }, sequenceNumber) => ({
          event,
          ...(negate ? { negate: true as const } : {}),
          sequenceNumber,
        })),
        type: 'sequence',
      }
  }
}
